'use client';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { cmsFetcher } from '@packages/shared/src/utils/cmsFetcher/cmsFetcher';
import { useIntl } from 'react-intl';
import useSWR from 'swr';
import { messages } from './Voucher.messages';
import { VoucherItem } from './VoucherItem.client';
import type { VoucherItemType, VoucherType } from './types';

/**
 * Component that displays active or expired coupons
 *
 * @param data Object contains data
 * @returns JSX.Element to be placed in PageLayout
 */
const VoucherInit = ({ promotionStatus }: { promotionStatus: string }) => {
  const { formatMessage, formatDate, locale } = useIntl();
  const {
    voucher: { apiEndpoints },
  } = useConfig();
  const isExpired = promotionStatus === 'expired';

  const { data, isLoading } = useSWR<VoucherItemType[]>(
    `${apiEndpoints.api}/${promotionStatus}?locale=${locale}`,
    cmsFetcher,
  );

  if (isLoading) {
    return (
      <div className="flex size-full items-center justify-center">
        <CircularProgress mode="indeterminate" className="h-6 w-6" key="vouchersLoading" />
      </div>
    );
  }

  if (data && data.length > 0) {
    return (
      <div className="bg-color-grey-light flex flex-col items-center gap-4 p-4">
        {data.map((voucher: VoucherItemType) => {
          const { voucherValue, validUntil, minOrderValue, customerGroup } = voucher;
          const detailInfos = [
            { key: formatMessage(messages.voucherValue), value: voucherValue ?? '' },
            {
              key: formatMessage(messages.voucherValidUntil),
              // multiply by 1000 here becaurse the php-timestamp (which we get here) is shortened by 1000 in difference to the JavaScript-timestamp
              value:
                (validUntil && formatDate(new Date(Number.parseInt(validUntil, 10) * 1000))) || '',
            },
            { key: formatMessage(messages.voucherMinOrderValue), value: minOrderValue ?? '' },
            { key: formatMessage(messages.voucherCustomerGroup), value: customerGroup ?? '' },
          ];

          const finalData = {
            ...voucher,
            detailInfos,
          };
          return (
            <VoucherItem key={voucher.drupal_id} voucherData={finalData} expired={isExpired} />
          );
        })}
      </div>
    );
  }

  return (
    <div className="bg-color-grey-light flex flex-col items-center gap-4 p-4">
      <span className="text-size-headline-xl">
        {isExpired
          ? formatMessage(messages.noDataForExpired)
          : formatMessage(messages.noDataForActive)}
      </span>
    </div>
  );
};

export const Voucher = ({ data }: { data: VoucherType }) => {
  const { formatMessage } = useIntl();
  const { field_promotion_status: promotionStatus } = data;
  // Render an ErrorBoundary component to catch errors and display an error message
  // If an error occurs, render a Typography component with the error message
  return (
    <ErrorBoundary
      fallback={
        <span className="text-size-headline-xl">{formatMessage(messages.errorLoading)}</span>
      }
    >
      <VoucherInit promotionStatus={promotionStatus} />
    </ErrorBoundary>
  );
};
